import type { SearchPatternsParams } from '@/entities/pattern';

import type { PatternFilterSchema } from '../model/types/pattern-filter.schema';

interface SortingParams {
    sort_by?: string;
    sort_order?: 'asc' | 'desc';
}

type MergeObjects<TSource, TTarget> = {
    [K in keyof TTarget | keyof TSource]: K extends keyof TTarget &
        keyof TSource
        ? TTarget[K] extends object
            ? TSource[K] extends object
                ? MergeObjects<TTarget[K], TSource[K]>
                : TSource[K]
            : TSource[K]
        : K extends keyof TTarget
          ? TTarget[K]
          : K extends keyof TSource
            ? TSource[K]
            : never;
};

function deepMerge<TSource extends object, TTarget extends object>(
    source: TSource,
    target: TTarget,
): MergeObjects<TSource, TTarget> {
    if (!source) {
        return target as MergeObjects<TSource, TTarget>;
    }

    if (!target) {
        return source as MergeObjects<TSource, TTarget>;
    }

    return Object.keys({ ...source, ...target }).reduce((accumulator, key) => {
        const sourceValue = source[key as keyof TSource];
        const targetValue = target[key as keyof TTarget];

        if (
            typeof sourceValue === 'object' &&
            sourceValue !== null &&
            !Array.isArray(sourceValue) &&
            typeof targetValue === 'object' &&
            targetValue !== null &&
            !Array.isArray(targetValue)
        ) {
            return {
                ...accumulator,
                [key]: deepMerge(sourceValue, targetValue),
            };
        }

        return {
            ...accumulator,
            [key]:
                key in target && targetValue !== undefined
                    ? targetValue
                    : sourceValue,
        };
    }, {}) as MergeObjects<TSource, TTarget>;
}

/**
 * Transforms filters and optional sorting into search parameters for API requests
 * @param baseFilter - Base filter (defined in the component)
 * @param filterState - Filter state (defined by the user)
 * @param sorting - Optional sorting parameters
 */
export const transformFiltersToSearchParams = (
    baseFilter: SearchPatternsParams = {},
    filterState: PatternFilterSchema = { filters: {} },
    sorting?: SortingParams,
): SearchPatternsParams => {
    if (baseFilter === null || baseFilter === undefined) {
        return {
            ...(filterState?.filters || {}),
            ...(sorting || {}),
        };
    }

    const filterValues = filterState?.filters || {};

    const mainFilters = deepMerge(
        baseFilter,
        filterValues,
    ) as SearchPatternsParams;

    // Add sorting params if provided
    if (sorting) {
        return {
            ...mainFilters,
            ...sorting,
        } as SearchPatternsParams;
    }

    return mainFilters;
};
